import React, {ForwardedRef, useEffect, useState} from 'react';
import cx from "classnames";

const SLOT_COUNT = 5;

const getSlot = (slotId: number, slots, maxBadgesCount: number) => {
    if (!slots) return null;

    if (slotId < 0) return null;

    const safeSlotId = slotId % maxBadgesCount;

    return slots[safeSlotId] || null;
}

interface BadgeCarouselProps {
    children: React.ReactNode;
    className?: string;
    badgeWidth?: number;
    speed?: number;
    size?: number;
    maxBadgesCount?: number;
}

const getBadgeSlotCount = (badgeCarouselRef: any, badgeWidth: number = 80, maxBadgesCount: number = 10) => {
    if (!badgeCarouselRef?.current) return 0; // it's performant to return 0 here, because it will be called on every render

    const carouselWidth = badgeCarouselRef.current?.clientWidth;

    if (!carouselWidth) {
        return 1;
    }

    const maxBadges = Math.floor(carouselWidth / badgeWidth);

    const minCount = Math.min(maxBadges, maxBadgesCount);

    return minCount > 0 ? minCount : 1;
};

export default function BadgeCarousel({children, className, speed = 1500, badgeWidth, maxBadgesCount = SLOT_COUNT}: BadgeCarouselProps) {
    const badgeCarouselRef = React.useRef<HTMLDivElement>(null);

    const [slotCount, setSlotCount] = useState<number>(getBadgeSlotCount(badgeCarouselRef, badgeWidth, maxBadgesCount));

    const [slots, setSlots] = useState<any[]>([]);

    useEffect(() => {
        if (!children) return;

        const newSlots = React.Children.toArray(children);

        setSlots(newSlots);
    }, [children]);

    const [pauseOnHover, setPauseOnHover] = useState(false);

    useEffect(() => {
        if (!badgeCarouselRef.current) return;

        const observer = new ResizeObserver(() => {
            const calculatedSlotCount =  getBadgeSlotCount(badgeCarouselRef, badgeWidth, maxBadgesCount);

            setSlotCount(calculatedSlotCount);
        });

        observer.observe(badgeCarouselRef.current);
    }, [badgeCarouselRef.current, children, badgeWidth, maxBadgesCount]);

    useEffect(() => {
        if (pauseOnHover || !slots.length || slots.length <= slotCount) {
            return;
        }

        const timeout = setInterval(() => {
            setSlots((slots) => {
                const newSlots = [...slots];
                const firstSlot = newSlots.shift();
                newSlots.push(firstSlot);

                return newSlots;
            });
        }, speed);

        return () => {
            clearTimeout(timeout);
        }
    }, [pauseOnHover !== undefined ? pauseOnHover : false, slots?.length, slotCount]);

    return (
        <div ref={badgeCarouselRef} className={cx("relative min-h-5 flex flex-row overflow-hidden max-w-full max-h-full", className)}
             onMouseOver={() => setPauseOnHover(true)} onMouseOut={() => setPauseOnHover(false)}>
            {
                Array.from(Array(slotCount || 0).keys()).map((_, index) => {
                    return (
                        <div key={`slot-${index}`}
                             className={cx("badge-carousel-slot flex flex-1 justify-center items-center border-gray-500 overflow-hidden", {'border-r': (index + 1) < slotCount})}>
                            {getSlot(index, slots, maxBadgesCount)}
                        </div>
                    )
                })
            }
        </div>
    );
}